import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Ticket, Coins, Sparkles, Gift, Calendar, Share2, MessageCircle, Loader2 } from "lucide-react";
import { trackConversion, trackMetaEvent, trackMetaCustom, runOnce } from "@/services/analytics";
import { capture as posthogCapture } from "@/services/posthog";
import FallingArtObjects from "@/components/thank-you/FallingArtObjects";
import FriendEmailForm from "@/components/thank-you/FriendEmailForm";
import BringFriendPanel from "@/components/event/booking/BringFriendPanel";
import { inviteFriendToEvent } from "@/lib/invitation";
import { useProfile } from "@/hooks/useProfile";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTracking } from "@/hooks/useInteractionTracking";
import { formatPrice } from "@/lib/currency";
import { useBringFriendEligibility } from "@/hooks/useBringFriend";
import { useHostProfiles } from "@/hooks/useHostProfiles";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { googleCalendarLink } from "@shared/departure";
import { BOOKED_GROUP_SELECT, bookedHostIds, toBookedDeparture, type BookedGroup } from "@/lib/bookedDeparture";
import { formatGroupTime } from "@/lib/eventGroups";

type PurchaseType = "ticket" | "credit_pack" | "campaign_package" | "advent_pass" | "trial_subscription";

interface PurchaseConfig {
  icon: React.ReactNode;
  title: string;
  getMessage: (params: URLSearchParams) => string;
  primaryCta: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
}

const purchaseConfigs: Record<PurchaseType, PurchaseConfig> = {
  ticket: {
    icon: <Ticket className="h-12 w-12 text-success" />,
    title: "You're Going!",
    getMessage: (params) => {
      const seats = params.get("seats") || "1";
      return `Your ${seats === "1" ? "ticket is" : `${seats} tickets are`} confirmed. Check your email for details.`;
    },
    primaryCta: { label: "View My Events", href: "/events" },
    secondaryCta: { label: "Invite a Friend", href: "/events" },
  },
  credit_pack: {
    icon: <Coins className="h-12 w-12 text-warning" />,
    title: "Credits Added!",
    getMessage: (params) => {
      const credits = params.get("credits") || "";
      return credits
        ? `${credits} credits have been added to your account.`
        : "Your credits have been added to your account.";
    },
    primaryCta: { label: "Browse Events", href: "/events" },
    secondaryCta: { label: "View Profile", href: "/profile" },
  },
  campaign_package: {
    icon: <Sparkles className="h-12 w-12 text-primary" />,
    title: "Welcome to Nimi!",
    getMessage: (params) => {
      const packageName = params.get("package") || "";
      return packageName
        ? `Your ${packageName} package is now active. Start exploring!`
        : "Your package is now active. Start exploring!";
    },
    primaryCta: { label: "Start Exploring", href: "/events" },
    secondaryCta: { label: "View Profile", href: "/profile" },
  },
  advent_pass: {
    icon: <Gift className="h-12 w-12 text-destructive" />,
    title: "Advent Pass Activated!",
    getMessage: () =>
      "Enjoy exclusive discounts on December events. Happy holidays!",
    primaryCta: { label: "View December Events", href: "/events" },
    secondaryCta: { label: "View Profile", href: "/profile" },
  },
  trial_subscription: {
    icon: <Sparkles className="h-12 w-12 text-primary" />,
    title: "You're In! 🎨",
    getMessage: (params) => {
      const packageName = params.get("package") || "your subscription";
      if (packageName.toLowerCase().includes("trial")) {
        return `Your creative adventure begins now! Enjoy your trial of ${packageName} — go explore something wonderful.`;
      }
      return `Welcome aboard! Your ${packageName} subscription is live. Time to fill your calendar with art and good vibes.`;
    },
    primaryCta: { label: "Explore Events", href: "/events" },
    secondaryCta: { label: "View My Membership", href: "/profile" },
  },
};

const ThankYou = () => {
  const [searchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { trackClick } = useTracking();
  const [friendEmailDone, setFriendEmailDone] = useState(false);
  
  const type = (searchParams.get("type") as PurchaseType) || "ticket";
  const amount = searchParams.get("amount");
  const eventId = searchParams.get("event_id");
  const forFriend = searchParams.get("forFriend") === "true";
  const seats = parseInt(searchParams.get("seats") || "1", 10);
  const method = searchParams.get("method");
  const currency = (searchParams.get("currency") || "gbp").toLowerCase();
  
  const config = purchaseConfigs[type] || purchaseConfigs.ticket;

  // Poll for ticket confirmation when coming from Stripe payment
  const isStripePayment = method === "stripe" && type === "ticket" && !!eventId;
  const [ticketConfirmed, setTicketConfirmed] = useState(!isStripePayment);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (!isStripePayment) return;

    const pollInterval = setInterval(async () => {
      pollCountRef.current += 1;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data } = await supabase
        .from('tickets')
        .select('id, status')
        .eq('event_id', eventId!)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setTicketConfirmed(true);
        clearInterval(pollInterval);
      } else if (pollCountRef.current >= 15) {
        // 15 * 2s = 30 seconds
        setPollingTimedOut(true);
        clearInterval(pollInterval);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [isStripePayment, eventId]);

  // PR5: Read server-generated purchase_event_id from URL.
  // The webhook persists this same UUID to the matching DB row, enabling Meta
  // Pixel <-> server CAPI dedup (PR6) within the 48h window.
  const purchaseEventId = searchParams.get('purchase_event_id') ?? undefined;

  // Paid Friends join at checkout. Stripe only sends users to return_url after a
  // successful payment, so this counts completed joins (not clicks). De-duped
  // per purchase_event_id so a refresh doesn't double count.
  useEffect(() => {
    if (searchParams.get('membership_join') !== '1' || !purchaseEventId) return;
    const key = `ph_membership_joined_${purchaseEventId}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
    posthogCapture('membership_joined_at_checkout', {
      event_id: eventId,
      discount_pct: Number(searchParams.get('discount_pct') || 0),
      seats: Number(searchParams.get('seats') || 1),
      early_bird_active: searchParams.get('early_bird') === '1',
      purchase_event_id: purchaseEventId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseEventId]);


  useEffect(() => {
    const amountValue = amount ? parseFloat(amount) : undefined;

    if (!purchaseEventId) {
      if (method !== 'stripe' && type === 'ticket') return; // credit/unlimited bookings have no purchase_event_id
      console.error(
        '[ThankYou] Missing purchase_event_id query param — Pixel Purchase event SKIPPED. ' +
          'This indicates a broken success_url. Type:',
        type,
        'Amount:',
        amount,
      );
      return;
    }

    // Track Meta Pixel Purchase event (with dedup eventID), once per purchase
    runOnce(`meta_purchase_${purchaseEventId}`, () =>
      trackMetaEvent(
        "Purchase",
        {
          value: amountValue,
          currency: currency.toUpperCase(),
          content_type: type,
        },
        purchaseEventId,
      ),
    );

    // Track Google Analytics conversion
    trackConversion(
      amountValue,
      currency.toUpperCase(),
      { product_type: type },
      purchaseEventId,
    );

    // PostHog: fire on mount alongside Meta/GA so counts reconcile 1:1.
    // Webhook-lag-driven polling timeouts would otherwise cause PostHog to
    // undercount. `booking_confirmed` (below) is the post-polling signal.
    posthogCapture("checkout_completed", {
      purchase_type: type,
      purchase_event_id: purchaseEventId,
      value: amountValue,
      currency: currency.toUpperCase(),
      event_id: eventId ?? undefined,
    });

    if (type === "credit_pack") {
      posthogCapture("credit_pack_purchased", {
        value: amountValue,
        credits: searchParams.get("credits") ?? undefined,
      });
    } else if (type === "trial_subscription") {
      posthogCapture("membership_started", {
        package: searchParams.get("package") ?? undefined,
        is_trial: true,
      });
    } else if (type === "advent_pass") {
      posthogCapture("advent_pass_purchased", { value: amountValue });
    } else if (type === "campaign_package") {
      posthogCapture("campaign_package_purchased", {
        package: searchParams.get("package") ?? undefined,
        value: amountValue,
      });
    }

    console.log(
      `Thank you page loaded - Type: ${type}, Amount: ${amount}, eventID: ${purchaseEventId}`,
    );
  }, [type, amount, purchaseEventId, currency, eventId, searchParams, method]);

  // Meta BookingConfirmed: every confirmed ticket booking, any payment method.
  const bookingRef = searchParams.get('booking_ref') ?? undefined;
  useEffect(() => {
    if (type !== 'ticket') return;
    const ref = purchaseEventId ?? bookingRef;
    if (!ref) return;
    runOnce(`meta_booking_confirmed_${ref}`, () =>
      trackMetaCustom(
        'BookingConfirmed',
        {
          content_ids: eventId ? [eventId] : [],
          content_type: 'product',
          num_items: seats,
          method: method ?? '',
        },
        ref,
      ),
    );
  }, [type, purchaseEventId, bookingRef, eventId, seats, method]);

  // booking_confirmed fires after Stripe ticket polling resolves — separate
  // funnel signal for anyone who wants to gate on confirmed webhook state.
  const bookingConfirmedFiredRef = useRef(false);
  useEffect(() => {
    if (ticketConfirmed && !bookingConfirmedFiredRef.current && type === "ticket") {
      bookingConfirmedFiredRef.current = true;
      posthogCapture("booking_confirmed", {
        purchase_type: type,
        purchase_event_id: purchaseEventId,
        event_id: eventId ?? undefined,
      });
    }
  }, [ticketConfirmed, type, purchaseEventId, eventId]);

  // Fetch event's WhatsApp group link for ticket purchases
  const { data: eventData } = useQuery({
    queryKey: ['event-chat', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data } = await supabase
        .from('events')
        .select('whatsapp_group, title, description, start_time, end_time, venue, timezone, host_id, secondary_host_id')
        .eq('id', eventId)
        .maybeSingle();
      return data;
    },
    enabled: !!eventId && type === 'ticket',
  });

  const { data: bringFriendEligibility } = useBringFriendEligibility(eventId ?? undefined);

  // Fetch the confirmed ticket for friend details and Bring a Friend invites.
  const { data: ticketData } = useQuery({
    queryKey: ['user-ticket-thankyou', eventId],
    queryFn: async () => {
      if (!eventId) return null;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase
        .from('tickets')
        .select(`id, group_id, ${BOOKED_GROUP_SELECT}`)
        .eq('event_id', eventId)
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data as unknown as { id: string; group_id: string | null; event_groups: BookedGroup | null } | null;
    },
    enabled: !!eventId && type === 'ticket',
  });

  const bookedGroup = ticketData?.event_groups ?? null;
  const { data: hostMap } = useHostProfiles(bookedGroup ? bookedHostIds(eventData, [bookedGroup]) : []);
  const departure = eventData?.start_time
    ? toBookedDeparture(eventData, bookedGroup, hostMap)
    : null;
  const chatLink = departure?.resolved.whatsapp_group || eventData?.whatsapp_group || null;
  const eventTz = eventData?.timezone || "Europe/London";

  const handleJoinChat = () => {
    if (chatLink) {
      trackClick("whatsapp_event_chat_thankyou", { eventId, type });
      window.open(chatLink, '_blank');
    }
  };

  const handleAddToCalendar = () => {
    if (!departure || !eventData) return;
    trackClick("add_to_calendar_thankyou", { eventId });
    window.open(googleCalendarLink({
      title: eventData.title ?? "",
      start: departure.resolved.start_time,
      end: departure.resolved.end_time || departure.resolved.start_time,
      details: eventData.description,
      location: departure.resolved.venue,
      groupName: departure.resolved.group_name,
    }), '_blank');
  };

  const handleInviteFriend = () => {
    if (eventId && profile?.referral_code) {
      trackClick("invite_friend_thankyou", { eventId, type });
      inviteFriendToEvent({ eventId, referralCode: profile.referral_code });
    }
  };

  const title = forFriend && type === "ticket" ? "Additional Tickets Booked!" : config.title;
  const showFriendEmailForm = forFriend && type === "ticket" && eventId && !friendEmailDone;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-start px-4 pt-8 pb-48 relative overflow-x-hidden">
      {/* Falling art objects with physics */}
      <FallingArtObjects cardRef={cardRef} />
      
      <Card ref={cardRef} className="w-full max-w-md p-8 text-center space-y-6 relative z-10 animate-scale-in">
        {/* Success/Polling Icon */}
        <div className="flex justify-center">
          <div className="relative">
            {!ticketConfirmed && !pollingTimedOut ? (
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
            ) : (
              <>
                {config.icon}
                <CheckCircle className="h-6 w-6 text-success absolute -bottom-1 -right-1 bg-background rounded-full" />
              </>
            )}
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground">
          {!ticketConfirmed && !pollingTimedOut ? "Confirming Your Booking..." : title}
        </h1>

        {/* Message */}
        <p className="text-muted-foreground">
          {!ticketConfirmed && !pollingTimedOut
            ? "Please wait while we confirm your payment..."
            : pollingTimedOut && !ticketConfirmed
            ? "Your booking is being processed. You'll receive an email confirmation shortly."
            : config.getMessage(searchParams)}
        </p>

        {/* Amount Display */}
        {amount && (
          <div className="py-2 px-4 bg-muted rounded-lg inline-block">
            <span className="text-sm text-muted-foreground">Amount paid: </span>
            <span className="font-semibold text-foreground">{formatPrice(parseFloat(amount), currency)}</span>
          </div>
        )}

        {/* Friend Email Form */}
        {showFriendEmailForm && (
          <FriendEmailForm
            seats={seats}
            ticketId={ticketData?.id}
            eventId={eventId}
            onDone={() => setFriendEmailDone(true)}
          />
        )}

        {ticketConfirmed && type === "ticket" && eventId && ticketData?.id && !forFriend && bringFriendEligibility?.eligible && (
          <BringFriendPanel
            eventId={eventId}
            ticketId={ticketData.id}
            eventTitle={eventData?.title ?? ""}
            embedded
          />
        )}

        {/* Assigned departure — grouped tickets only; ungrouped stays as before */}
        {ticketConfirmed && type === "ticket" && bookedGroup && departure && (
          <div className="rounded-lg border border-border bg-muted/50 p-4 text-left space-y-2">
            <div className="text-sm font-semibold text-foreground">
              {departure.resolved.group_name ? `${departure.resolved.group_name} · ` : ""}
              {formatGroupTime(departure.resolved.start_time, eventTz)}
            </div>
            {departure.resolved.venue && (
              <p className="text-sm text-muted-foreground">Meeting point: {departure.resolved.venue}</p>
            )}
            {departure.hosts.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {departure.hosts.map((h) => (
                    <Avatar key={h.id} className="h-6 w-6 border border-background">
                      <AvatarImage src={h.avatar_url ?? undefined} />
                      <AvatarFallback className="text-[10px]">{(h.first_name || "?")[0]}</AvatarFallback>
                    </Avatar>
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  Hosted by {departure.hosts.map((h) => h.first_name).filter(Boolean).join(" & ")}
                </span>
              </div>
            )}
          </div>
        )}

        {/* CTA Buttons */}
        <div className="space-y-3 pt-2">
          {type === "ticket" ? (
            <Button
              className="w-full"
              size="lg"
              onClick={handleJoinChat}
              disabled={!chatLink}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Join Event Chat
            </Button>
          ) : (
            null
          )}
          {type === "ticket" && ticketConfirmed && bookedGroup && departure && (
            <Button variant="outline" className="w-full" onClick={handleAddToCalendar}>
              <Calendar className="h-4 w-4 mr-2" />
              Add to Calendar
            </Button>
          )}
          {type !== "ticket" && (
            <Link to={config.primaryCta.href} className="block">
              <Button className="w-full" size="lg">
                <Calendar className="h-4 w-4 mr-2" />
                {config.primaryCta.label}
              </Button>
            </Link>
          )}
          
          {type === "ticket" && eventId && (
            <>
              {!forFriend && !bringFriendEligibility?.eligible && (
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={handleInviteFriend}
                  disabled={profileLoading || !profile?.referral_code}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Invite Friend (£5 Off)
                </Button>
              )}
            </>
          )}
          {type !== "ticket" && config.secondaryCta && (
            <Link to={config.secondaryCta.href} className="block">
              <Button variant="outline" className="w-full">
                {config.secondaryCta.label}
              </Button>
            </Link>
          )}
        </div>
      </Card>

      {/* Footer */}
      <p className="text-sm text-muted-foreground mt-6">
        Questions? Contact us at hello@nimi.live or via Whatsapp.
      </p>
    </div>
  );
};

export default ThankYou;
